// Embedded SSO — when Prezync runs inside the AKUTracker iframe (Study ›
// Presentations), sign the user in automatically with a Firebase custom token
// that AKUTracker mints from its own (Supabase) identity. No second login,
// no personal Google account.
//
// Handshake (postMessage):
//   child → parent : { source:'prezync',    type:'sso-request' }
//   parent → child : { source:'akutracker', type:'sso-token', token }
//
// The user AKUTracker signs you in as is `aku_<akutracker-user-id>` — a
// dedicated Firebase identity, distinct from any personal Google account. Each
// AKUTracker user gets their own, so decks/sessions are isolated per person.
//
// Safety (this is the important bit): when embedded we must NEVER leave a
// cached *personal* session (e.g. a Google login on prezync.com)
// showing inside the AKUTracker tab. So:
//   · we retry the token request until it lands (parent may bind late), and
//   · if a non-`aku_` user is signed in and no token has arrived, we sign it
//     out — better an empty state than someone's real account leaking in.
//
// No-op unless actually framed by another window (never top-level or in Tauri).

import { auth } from './firebase.js';
import { signInWithCustomToken, onAuthStateChanged, signOut } from 'firebase/auth';

const AKU_ORIGINS = [
  'https://akutracker.app',
  'https://www.akutracker.app',
];

function isAllowedParentOrigin(origin) {
  if (AKU_ORIGINS.includes(origin)) return true;
  // Dev: localhost / 127.0.0.1 on any port (Live Server, vite preview, etc.)
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);
}

const MAX_ATTEMPTS = 12;   // ~7s of retries at 600ms
const RETRY_MS = 600;

let _initialised = false;

export function initEmbeddedSso() {
  if (_initialised) return;
  _initialised = true;

  // Only when framed by a *different* window. Top-level or Tauri → do nothing,
  // so standalone use and audience-join flows are untouched.
  if (typeof window === 'undefined' || window.parent === window) return;

  let ssoDone = false;
  let attempts = 0;
  let retryTimer = null;

  // Guard: while we're waiting for the token, don't let a cached personal
  // (non-aku_) session sit visible in the embed. Real AKUTracker users have no
  // such session; this only bites a developer who also uses Prezync standalone.
  const stopWatch = onAuthStateChanged(auth, (u) => {
    if (ssoDone) return;
    if (u && !u.uid.startsWith('aku_')) {
      signOut(auth).catch(() => {});
    }
  });

  function askForToken() {
    if (ssoDone) return;
    attempts += 1;
    try {
      window.parent.postMessage({ source: 'prezync', type: 'sso-request' }, '*');
    } catch (e) { /* parent rejected postMessage — will retry */ }
    if (attempts < MAX_ATTEMPTS) {
      retryTimer = setTimeout(askForToken, RETRY_MS);
    } else if (auth.currentUser && !auth.currentUser.uid.startsWith('aku_')) {
      // Gave up and a personal session is showing — clear it rather than leak.
      signOut(auth).catch(() => {});
      console.warn('[embed-sso] no SSO token from parent after retries; signed out to avoid showing a personal account.');
    }
  }

  window.addEventListener('message', async (ev) => {
    if (!isAllowedParentOrigin(ev.origin)) return;
    const d = ev.data;
    if (!d || d.source !== 'akutracker') return;

    if (d.type === 'sso-token' && d.token && !ssoDone) {
      try {
        await signInWithCustomToken(auth, d.token);
        ssoDone = true;
        if (retryTimer) clearTimeout(retryTimer);
        stopWatch();
        // watchAuth() in main.js now sees the aku_ user and routes to /dashboard.
        // Start streaming decks/sessions to the (validated) parent for its
        // native gallery. Lazy-loaded so it only ships when actually embedded.
        import('./embed-relay.js')
          .then((m) => m.startEmbedRelay(ev.origin))
          .catch((e) => console.warn('[embed-sso] relay start failed:', e));
      } catch (err) {
        console.warn('[embed-sso] custom-token sign-in failed:', err);
      }
    } else if (d.type === 'sso-error') {
      console.warn('[embed-sso] parent could not mint a token:', d.error);
    }
  });

  askForToken();
}
